import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import InstallAppButton from "./InstallAppButton";
import { SITE } from "./config";
import { CITIES } from "./cities";
import { Menu, X, MapPin, Phone, Mail, UserCog, Sparkles, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/dechets", label: "Nos déchets" },
  { to: "/comment-ca-marche", label: "Comment ça marche" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

export default function SiteLayout({ children }) {
  const [open, setOpen] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const startDemo = async () => {
    setDemoLoading(true);
    try {
      await api.post("/auth/demo-login");
      window.location.href = "/mon-espace";
    } catch (err) {
      setDemoLoading(false);
      alert("Impossible de démarrer la démo : " + (err?.message || "erreur inconnue"));
    }
  };

  return (
    <div className="min-h-screen bg-[#fafdf6] text-stone-900 font-body flex flex-col">
      {/* Top contact bar */}
      <div className="bg-[#1F6B2E] text-white text-xs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            <a href={`tel:${SITE.phoneRaw}`} className="inline-flex items-center gap-1.5 hover:text-[#9DCB2E] transition-colors font-bold">
              <Phone className="w-3.5 h-3.5" strokeWidth={2.8} /> {SITE.phone}
            </a>
            <Link to="/contact" data-testid="topbar-contact-link" className="hidden sm:inline-flex items-center gap-1.5 hover:text-[#9DCB2E] transition-colors">
              <Mail className="w-3.5 h-3.5" strokeWidth={2.8} /> {SITE.email}
            </Link>
            <span className="hidden md:inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" strokeWidth={2.8} /> {SITE.address.full}
            </span>
          </div>
          <InstallAppButton variant="topbar" />
        </div>
      </div>

      {/* Main header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" data-testid="site-logo" className="flex items-center gap-3" onClick={() => setOpen(false)}>
            <Logo size={48} withText />
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-bold transition-colors ${
                    isActive ? "text-[#1F6B2E] bg-[#9DCB2E]/15" : "text-stone-700 hover:text-[#1F6B2E] hover:bg-stone-50"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={startDemo}
              disabled={demoLoading}
              data-testid="nav-demo-btn"
              className="inline-flex items-center gap-1.5 bg-[#9DCB2E] hover:bg-[#86b323] disabled:opacity-60 text-[#0f3a18] font-black px-4 py-2 rounded-full text-sm transition-all active:scale-95 shadow-lg shadow-[#9DCB2E]/40"
            >
              <Sparkles className="w-4 h-4" strokeWidth={2.8} fill="currentColor" />
              {demoLoading ? "Lancement..." : "Démo"}
            </button>
            <Link
              to={user ? "/mon-espace" : "/connexion"}
              data-testid="site-espace-link"
              className="hidden sm:inline-flex items-center gap-1.5 text-stone-700 hover:text-[#1F6B2E] hover:bg-stone-50 font-bold px-3 py-2 rounded-full text-sm transition-colors"
            >
              <UserCog className="w-4 h-4" strokeWidth={2.5} />
              {user ? "Mon espace" : "Espace commercial"}
            </Link>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2 rounded-full hover:bg-stone-100"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-stone-200 bg-white">
            <nav className="max-w-6xl mx-auto px-4 py-2 flex flex-col">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-xl font-bold ${
                      isActive ? "text-[#1F6B2E] bg-[#9DCB2E]/15" : "text-stone-700 hover:bg-stone-50"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to={user ? "/mon-espace" : "/connexion"}
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#1F6B2E] text-white font-bold px-4 py-3 rounded-full"
              >
                <UserCog className="w-4 h-4" strokeWidth={2.8} /> {user ? "Mon espace" : "Espace commercial"}
              </Link>
              {!user && (
                <button
                  type="button"
                  onClick={() => { setOpen(false); startDemo(); }}
                  disabled={demoLoading}
                  data-testid="site-demo-btn-mobile"
                  className="mt-2 inline-flex items-center justify-center gap-2 bg-[#9DCB2E] text-[#0f3a18] font-black px-4 py-3 rounded-full"
                >
                  <Sparkles className="w-4 h-4" strokeWidth={2.8} />
                  {demoLoading ? "Lancement..." : "Lancer la démo"}
                </button>
              )}
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate("/");
              }
            }}
            data-testid="site-back-btn"
            aria-label="Retour à la page précédente"
            className="group inline-flex items-center gap-1.5 text-stone-500 hover:text-[#1F6B2E] text-sm font-medium transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" strokeWidth={1.5} />
            <span className="lowercase tracking-wide">retour</span>
          </button>
        </div>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#0f3a18] text-stone-200 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="bg-white p-3 rounded-2xl inline-block mb-3">
              <Logo size={40} withText />
            </div>
            <p className="text-sm text-stone-300 leading-relaxed">
              Centre de recyclage professionnel à Saint-Victoret.
              On trie, on valorise, on certifie.
            </p>
          </div>
          <div>
            <h4 className="font-heading font-black text-white mb-3 text-sm uppercase tracking-wider">Navigation</h4>
            <ul className="space-y-2 text-sm">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="hover:text-[#9DCB2E] transition-colors">
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-black text-white mb-3 text-sm uppercase tracking-wider">Contact</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href={`tel:${SITE.phoneRaw}`} className="inline-flex items-center gap-2 hover:text-[#9DCB2E]">
                  <Phone className="w-4 h-4" strokeWidth={2.5} /> {SITE.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${SITE.email}`} className="inline-flex items-center gap-2 hover:text-[#9DCB2E] break-all">
                  <Mail className="w-4 h-4 shrink-0" strokeWidth={2.5} /> {SITE.email}
                </a>
              </li>
              <li className="inline-flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5} /> {SITE.address.full}
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-black text-white mb-3 text-sm uppercase tracking-wider">Horaires</h4>
            <div className="text-sm text-stone-200">
              <div>Du lundi au vendredi</div>
              <div className="mt-1 font-heading font-black text-2xl text-[#9DCB2E] leading-tight">
                7h — 17h
                <span className="ml-2 align-middle inline-block bg-[#9DCB2E] text-[#0f3a18] text-[9px] uppercase tracking-[0.15em] font-black px-2 py-0.5 rounded-full">
                  Non-stop
                </span>
              </div>
              <div className="mt-2 text-xs text-stone-400">Fermé samedi et dimanche</div>
            </div>
          </div>
        </div>

        {/* SEO local links — maillage interne pour ranker sur les requêtes locales */}
        <div className="border-t border-white/10 bg-[#0a2e12]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
            <h3 className="font-heading font-black text-white text-sm uppercase tracking-wider mb-4">
              Recyclage par ville
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-sm">
              {CITIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/recyclage/${c.slug}`}
                  className="text-stone-300 hover:text-[#9DCB2E] transition-colors"
                >
                  Recyclage {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 text-xs text-stone-400 flex flex-wrap items-center justify-between gap-2">
            <span>© {new Date().getFullYear()} FB Environnement — Tous droits réservés</span>
            <span>Centre de recyclage agréé · SIRET sur demande</span>
          </div>
        </div>
      </footer>

      {/* Bouton flottant : retour à la grille tarifaire pour le commercial connecté */}
      {user && (
        <Link
          to="/mon-espace"
          data-testid="floating-back-to-crm"
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-[#9DCB2E] hover:bg-[#86b323] text-[#0f3a18] font-black px-4 py-3 rounded-full shadow-xl shadow-emerald-500/30 transition-all active:scale-95"
          title="Retour à ma grille tarifaire"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={3} />
          <span className="text-sm">Ma grille tarifaire</span>
        </Link>
      )}
    </div>
  );
}
